/**
 * Camera transform math.
 *
 * Coordinate systems:
 *   - World space: galaxy coordinates in [0, 1] × [0, 1] (how systems are
 *     authored in scenario.ts).
 *   - View space:  a rectangle inside the canvas, inset by the HUD + side
 *     panel paddings. (The renderer hands us a Viewport describing this.)
 *   - Screen space: CSS pixels on the canvas element.
 *
 * The galaxy view rectangle is (padLeft, padTop) → (padLeft+viewW, padTop+viewH).
 * At zoom=1, world-coord (centerX, centerY) is rendered at the viewport centre;
 * the whole unit square maps to a viewW×viewH rectangle.
 *
 * Zoom scales around the camera centre. worldToScreen and screenToWorld are
 * inverses; tests lock that in.
 */

import type { Camera } from '../ui/state';

export interface Viewport {
  /** Screen-space position of the view rectangle's top-left corner. */
  padLeft: number;
  padTop: number;
  /** Size of the view rectangle in screen pixels. */
  viewW: number;
  viewH: number;
}

export interface Vec2 {
  x: number;
  y: number;
}

export function worldToScreen(camera: Camera, vp: Viewport, world: Vec2): Vec2 {
  // Shift so camera centre is at origin, scale by zoom, then translate to
  // the viewport centre on screen.
  const relX = (world.x - camera.centerX) * camera.zoom;
  const relY = (world.y - camera.centerY) * camera.zoom;
  return {
    x: vp.padLeft + vp.viewW * 0.5 + relX * vp.viewW,
    y: vp.padTop + vp.viewH * 0.5 + relY * vp.viewH,
  };
}

export function screenToWorld(camera: Camera, vp: Viewport, screen: Vec2): Vec2 {
  const relX = (screen.x - (vp.padLeft + vp.viewW * 0.5)) / vp.viewW;
  const relY = (screen.y - (vp.padTop + vp.viewH * 0.5)) / vp.viewH;
  return {
    x: camera.centerX + relX / camera.zoom,
    y: camera.centerY + relY / camera.zoom,
  };
}

/**
 * Zoom toward a screen-space anchor point (typically the mouse cursor) by the
 * given factor, keeping the world-point under the anchor stationary. Returns
 * an updated Camera; does NOT mutate the input.
 */
export function zoomAt(
  camera: Camera,
  vp: Viewport,
  anchorScreen: Vec2,
  factor: number,
  clampZoomFn: (z: number) => number,
): Camera {
  const anchorWorldBefore = screenToWorld(camera, vp, anchorScreen);
  const newZoom = clampZoomFn(camera.zoom * factor);
  // Build a candidate camera with the new zoom and then shift its centre so
  // that the anchor's world coordinate lands at the same screen position.
  const candidate: Camera = {
    centerX: camera.centerX,
    centerY: camera.centerY,
    zoom: newZoom,
  };
  const anchorScreenAfter = worldToScreen(candidate, vp, anchorWorldBefore);
  // Compensate for the drift: in world-space, the amount the anchor moved is
  // dx/viewW/zoom (same rearrangement worldToScreen uses).
  candidate.centerX += (anchorScreenAfter.x - anchorScreen.x) / (vp.viewW * newZoom);
  candidate.centerY += (anchorScreenAfter.y - anchorScreen.y) / (vp.viewH * newZoom);
  return candidate;
}

/**
 * Pan by a screen-space delta (e.g. mouse drag). Positive dx moves the view
 * right-ward, which corresponds to the camera centre moving LEFT-ward in
 * world coords.
 */
export function panByScreenDelta(camera: Camera, vp: Viewport, dx: number, dy: number): Camera {
  return {
    centerX: camera.centerX - dx / (vp.viewW * camera.zoom),
    centerY: camera.centerY - dy / (vp.viewH * camera.zoom),
    zoom: camera.zoom,
  };
}

/**
 * Find the nearest system to a screen-space point, within `threshold` pixels.
 * Returns null if no system is within threshold.
 */
export function hitTestSystems<TSys extends { id: number; x: number; y: number }>(
  camera: Camera,
  vp: Viewport,
  systems: Iterable<TSys>,
  screen: Vec2,
  threshold: number,
): TSys | null {
  let best: TSys | null = null;
  let bestDistSq = threshold * threshold;
  for (const sys of systems) {
    const s = worldToScreen(camera, vp, { x: sys.x, y: sys.y });
    const dx = s.x - screen.x;
    const dy = s.y - screen.y;
    const d2 = dx * dx + dy * dy;
    if (d2 <= bestDistSq) {
      bestDistSq = d2;
      best = sys;
    }
  }
  return best;
}
